import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { Box } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Slide, { SlideProps } from '@mui/material/Slide';
import MUISnackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { LONG_DURATION, SHORT_DURATION, SHORT_MESSAGE_MAX_LENGTH } from '../../constants/shell';
import variables from '../../contexts/ThemeProvider/variables';
import { removeSnackbarMessage } from '../../store/actions/snackbar';
import { selectSnackbarMessages } from '../../store/selectors/snackbar';
import { NotificationListEnum } from '../../types/shell';
import { ISnackbarMessage } from '../../types/snackbar';

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

export default function ConsecutiveSnackbars() {
  const snackbarMessages = useSelector(selectSnackbarMessages);
  const [open, setOpen] = useState(false);
  const [messageInfo, setMessageInfo] = useState<ISnackbarMessage | undefined>(undefined);

  const dispatch = useDispatch();

  useEffect(() => {
    const shouldShowNewMessage = snackbarMessages.length > 0 && !messageInfo;
    const shouldCloseCurrentMessage = snackbarMessages.length > 0 && messageInfo && open;

    if (shouldShowNewMessage) {
      setMessageInfo({ ...snackbarMessages[0] });
      dispatch(removeSnackbarMessage());
      setOpen(true);
    }

    if (shouldCloseCurrentMessage) {
      setOpen(false);
    }
  }, [snackbarMessages, messageInfo, open, dispatch]);

  const handleClose = useCallback((event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  }, []);

  const handleQuit = useCallback(() => {
    if (messageInfo?.onClose) {
      messageInfo.onClose();
    }
    setMessageInfo(undefined);
  }, [messageInfo]);

  const snackbarBackgroundColor = useMemo(() => {
    const { type } = messageInfo || {};
    switch (type) {
      case NotificationListEnum.Error:
        return variables.errorRed;
      case NotificationListEnum.Warning:
        return variables.toastWarning;
      case NotificationListEnum.Info:
        return variables.toastInfo;
      case NotificationListEnum.Success:
        return variables.toastSuccess;
      default:
        return variables.toastDefault;
    }
  }, [messageInfo]);

  const isLongMessage = useMemo(() => {
    if (typeof messageInfo?.message !== 'string') {
      return true;
    }
    return messageInfo && messageInfo?.message?.length > SHORT_MESSAGE_MAX_LENGTH;
  }, [messageInfo]);

  const isAlertNotification = useMemo(() => {
    return messageInfo?.type === NotificationListEnum.Error || messageInfo?.type === NotificationListEnum.Warning;
  }, [messageInfo]);

  const renderIcon = useMemo(() => {
    if (!messageInfo) {
      return null;
    }

    const isError = messageInfo.type === NotificationListEnum.Error;
    const isWarning = messageInfo.type === NotificationListEnum.Warning;
    const isInfo = messageInfo.type === NotificationListEnum.Info;
    const isSuccess = messageInfo.type === NotificationListEnum.Success;
    const isLoading = messageInfo.type === NotificationListEnum.Loading;

    if (isError) {
      return <ErrorOutlineIcon sx={{ marginRight: '8px' }} />;
    }

    if (isWarning) {
      return <ReportProblemOutlinedIcon sx={{ marginRight: '8px' }} />;
    }

    if (isInfo) {
      return <InfoOutlinedIcon sx={{ marginRight: '8px' }} />;
    }

    if (isSuccess) {
      return <CheckCircleOutlinedIcon sx={{ marginRight: '8px' }} />;
    }

    if (isLoading) {
      return (
        <Box>
          <CircularProgress sx={{ marginRight: '8px', color: variables.white }} size={20} />
        </Box>
      );
    }

    return null;
  }, [messageInfo]);

  return (
    <Box data-action="snackbar">
      <MUISnackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          transition: { onExited: handleQuit },
          content: {
            sx: {
              backgroundColor: snackbarBackgroundColor,
              color: variables.white,
              maxWidth: 388,
              display: 'flex',
              flexWrap: 'nowrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: 'row',
              whiteSpace: isLongMessage || isAlertNotification ? 'normal' : 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            },
          },
        }}
        key={messageInfo ? messageInfo.key : undefined}
        open={open}
        autoHideDuration={isLongMessage ? LONG_DURATION : SHORT_DURATION}
        onClose={handleClose}
        message={
          messageInfo ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {renderIcon}
              {messageInfo.message}
            </div>
          ) : undefined
        }
        slots={{ transition: SlideTransition }}
        sx={{
          '& .MuiSnackbarContent-action': {
            marginLeft: 0,
          },
        }}
        action={
          <React.Fragment>
            {(isLongMessage || isAlertNotification) && (
              <IconButton aria-label="close" color="inherit" sx={{ p: 0.5 }} onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            )}
          </React.Fragment>
        }
      />
    </Box>
  );
}
